import type { Artifact, DefinitionSet } from "@/lib/defs"
import { asArray, asString, isRecord } from "@/lib/utils"
import { localName, plural, sentenceCase, sharedPrefix, titleCase } from "./humanize"

/**
 * The business reading of an admitted definition set: human labels, field roles, typed links,
 * lifecycles and business areas. Everything here is derived from the definitions themselves —
 * declared Core value types, descriptions, importer metadata, links and lifecycles — with the
 * humanize fallbacks only where metadata is silent. Nothing names a tenant, app or record.
 */

export type FieldRole =
  | "title"
  | "identifier"
  | "status"
  | "money"
  | "currency"
  | "date"
  | "quantity"
  | "reference"
  | "contact"
  | "flag"
  | "text"
  | "technical"

export interface EnumOption {
  readonly value: string
  readonly label: string
  readonly description?: string
}

export interface FieldMeta {
  readonly id: string
  readonly label: string
  /** The primitive kind after resolving Core value-type references. */
  readonly kind: string
  /** The declared type as written: a Core value type (`core.money_amount`) or the primitive kind. */
  readonly typeName: string
  readonly role: FieldRole
  readonly description?: string
  readonly options: readonly EnumOption[]
  readonly markings: readonly string[]
  /** For references: the link this property carries the key of. */
  readonly link?: string
  /** Integer minor units (cents / piastres) of a money amount. */
  readonly minorUnits?: boolean
  /** The sibling property holding this amount's ISO 4217 currency. */
  readonly currencyField?: string
  /** The only currency the ontology declares anywhere, for amounts that carry none beside them. */
  readonly currencyDefault?: string
  /** For `_en`/`_ar` pairs: the language the value is written in. */
  readonly language?: "en" | "ar"
  /** Declared by an extension namespace rather than the type itself. */
  readonly extension?: string
}

export interface LinkMeta {
  readonly id: string
  readonly label: string
  readonly source: string
  readonly target: string
  readonly toMany: boolean
  readonly description?: string
  readonly extension?: string
}

export interface LifecycleMeta {
  readonly property: string
  readonly initial: readonly string[]
  readonly terminal: readonly string[]
  /** States in the order a record moves through them (initial first, terminal last). */
  readonly states: readonly string[]
  readonly transitions: readonly { readonly id: string; readonly from: readonly string[]; readonly to: string }[]
}

export interface TypeMeta {
  readonly name: string
  readonly namespace: string
  readonly label: string
  readonly pluralLabel: string
  readonly description?: string
  readonly layer: Artifact["layer"]
  readonly artifactId: string
  readonly fields: readonly FieldMeta[]
  readonly links: readonly LinkMeta[]
  readonly identity: readonly string[]
  readonly titleFields: readonly string[]
  readonly statusField?: string
  readonly dateField?: string
  readonly amountField?: string
  readonly lifecycle?: LifecycleMeta
  readonly implements: readonly string[]
  /** Where an imported type came from (`SourceSystem.Document`), when declared. */
  readonly source?: string
  readonly module?: string
}

export interface AreaMeta {
  readonly id: string
  readonly label: string
  readonly types: readonly string[]
  readonly core: boolean
  readonly apps: readonly { readonly appId: string; readonly entryPageId?: string }[]
}

export interface AreaRelation {
  readonly from: string
  readonly to: string
  readonly links: readonly LinkMeta[]
}

export interface Ontology {
  readonly types: ReadonlyMap<string, TypeMeta>
  readonly areas: readonly AreaMeta[]
  readonly relations: readonly AreaRelation[]
  /** Links pointing at a type, from every other type. */
  readonly incoming: ReadonlyMap<string, readonly LinkMeta[]>
  /** Metadata the business view had to fall back on (shown in Builder, never on business pages). */
  readonly gaps: readonly string[]
}

const TITLE_IDS = ["display_name", "title", "name_en", "name_ar", "label_en", "label_ar", "label", "full_name", "subject"]
const TECHNICAL_IDS = new Set(["idx", "synthetic", "docstatus", "owner", "modified_by", "creation", "modified", "result_key", "definition", "fresh_until"])
const STATUS_IDS = /^(status|state|stage|.*_status|.*_state)$/
const DATE_IDS = /^(date|.*_date|.*_on|.*_at|posting_date|due_date)$/
const MONEY_IDS = /^(amount|total|price|cost|fee|balance|.*_amount|.*_total|.*_price|.*_cost|.*_fee)$/
const QUANTITY_IDS = /^(qty|quantity|count|units|.*_qty|.*_quantity|.*_count)$/

/** `showcase__customer_code` (an extension member) → `customer_code`. */
function memberName(id: string): string {
  const i = id.indexOf("__")
  return i > 0 ? id.slice(i + 2) : id
}

/** Imported Frappe descriptions read `Label. Name of a X document. Source: System.Doc/field`. */
function importedLabel(description: string | undefined): string | undefined {
  if (!description || !/Source: /.test(description)) return undefined
  const first = description.split(/\.\s/)[0]?.trim()
  return first && first.length <= 60 && !first.startsWith("Source:") ? first : undefined
}

export function optionLabel(value: string): string {
  // Already written for people (`Not Contacted`, `Awaiting Quote Approval`), or a code (`EGP`, `WHATSAPP` stays readable).
  if (/\s/.test(value) && /[a-z]/.test(value)) return value
  if (/^[A-Z0-9]{2,4}$/.test(value)) return value
  // Identifiers (`DEMO-C-137`, `SO-0001`) are data, not words.
  if (/\d/.test(value) && /[-_/.]/.test(value)) return value
  return sentenceCase(value.toLowerCase() === value || value.toUpperCase() === value ? value.toLowerCase() : value)
}

interface PropertyTypeDef {
  readonly kind: string
  readonly name?: string
  readonly options: readonly EnumOption[]
  /** A monetary decimal's declared currency property (EntityIR `currencySource`, same object only). */
  readonly currencyProperty?: string
}

/** Core value types (`core.display_name`, `core.money_amount`, …) declared in catalog artifacts. */
function propertyTypeCatalog(set: DefinitionSet): Map<string, Record<string, unknown>> {
  const out = new Map<string, Record<string, unknown>>()
  for (const a of set.artifacts) {
    if (a.kind !== "entity") continue
    for (const pt of asArray(a.payload.propertyTypes)) {
      if (isRecord(pt) && typeof pt.name === "string" && isRecord(pt.type)) out.set(pt.name, pt.type)
    }
  }
  return out
}

function resolveType(raw: unknown, catalog: Map<string, Record<string, unknown>>, depth = 0): PropertyTypeDef {
  if (!isRecord(raw)) return { kind: "unknown", options: [] }
  const kind = asString(raw.kind) ?? "unknown"
  if (kind === "ref" && typeof raw.name === "string" && depth < 4) {
    const target = catalog.get(raw.name)
    const resolved = target ? resolveType(target, catalog, depth + 1) : { kind: "text", options: [] }
    return { ...resolved, name: raw.name }
  }
  const options = asArray(raw.values).flatMap((v) => {
    if (isRecord(v) && typeof v.value === "string")
      return [{ value: v.value, label: asString(v.label) ?? optionLabel(v.value), description: asString(v.description) }]
    if (typeof v === "string") return [{ value: v, label: optionLabel(v) }]
    return []
  })
  const source = isRecord(raw.currencySource) ? raw.currencySource : undefined
  const currencyProperty = source && asArray(source.links).length === 0 ? asString(source.property) : undefined
  return currencyProperty ? { kind, options, currencyProperty } : { kind, options }
}

interface RawProperty {
  readonly id: string
  readonly type: unknown
  readonly description?: string
  readonly markings: readonly string[]
  readonly extension?: string
}

interface RawLink {
  readonly id: string
  readonly target: string
  readonly toMany: boolean
  readonly description?: string
  readonly extension?: string
}

function readProperties(body: Record<string, unknown>, extension?: string): RawProperty[] {
  return asArray(body.properties).flatMap((p) =>
    isRecord(p) && typeof p.id === "string"
      ? [{ id: p.id, type: p.type, description: asString(p.description), markings: asArray(p.markings).filter((m): m is string => typeof m === "string"), extension }]
      : [],
  )
}

function readLinks(body: Record<string, unknown>, extension?: string): RawLink[] {
  return asArray(body.links).flatMap((l) => {
    if (!isRecord(l) || typeof l.id !== "string" || typeof l.target !== "string") return []
    const card = isRecord(l.cardinality) ? l.cardinality : {}
    return [{ id: l.id, target: l.target, toMany: card.to === "many", description: asString(l.description), extension }]
  })
}

function lifecycleOf(body: Record<string, unknown>): LifecycleMeta | undefined {
  const lc = asArray(body.lifecycles).find(isRecord)
  if (!lc || typeof lc.property !== "string") return undefined
  const strings = (v: unknown) => asArray(v).filter((s): s is string => typeof s === "string")
  const transitions = asArray(lc.transitions).flatMap((t) =>
    isRecord(t) && typeof t.to === "string" ? [{ id: asString(t.id) ?? t.to, from: strings(t.from), to: t.to }] : [],
  )
  const initial = strings(lc.initial)
  const terminal = strings(lc.terminal)
  // Breadth-first from the initial states gives the order a record usually moves in.
  const states: string[] = [...initial]
  for (let i = 0; i < states.length; i++) {
    for (const t of transitions) if (t.from.includes(states[i] as string) && !states.includes(t.to) && !terminal.includes(t.to)) states.push(t.to)
  }
  for (const t of transitions) if (!states.includes(t.to) && !terminal.includes(t.to)) states.push(t.to)
  for (const s of terminal) if (!states.includes(s)) states.push(s)
  return { property: lc.property, initial, terminal, states, transitions }
}

function classify(
  p: RawProperty,
  resolved: PropertyTypeDef,
  ctx: { identity: readonly string[]; links: readonly RawLink[]; lifecycle?: string; ids: ReadonlySet<string> },
): { role: FieldRole; link?: string; minorUnits?: boolean; language?: "en" | "ar" } {
  const id = memberName(p.id)
  const ref = resolved.name ?? ""
  const language = /_en$/.test(id) ? "en" : /_ar$/.test(id) ? "ar" : undefined
  const link = ctx.links.find((l) => {
    const linkId = memberName(l.id)
    return linkId === id || `${linkId}_id` === id || `${linkId}_code` === id || `${linkId}_key` === id || `${linkId}_name` === id
  })
  if (ctx.lifecycle === p.id) return { role: "status" }
  if (ctx.identity.includes(p.id)) return { role: "identifier" }
  if (link) return { role: "reference", link: link.id }
  if (TECHNICAL_IDS.has(id) || /_head$/.test(id) || resolved.kind === "json" || resolved.kind === "json-ext") return { role: "technical" }
  if (ref === "core.display_name" || TITLE_IDS.includes(id) || /^(name|label|title)_(en|ar)$/.test(id)) return { role: "title", language }
  if (ref === "core.code" || ref === "core.document_number") return { role: "identifier" }
  if (ref.startsWith("iso4217") || id === "currency" || /_currency$/.test(id)) return { role: "currency" }
  if (resolved.currencyProperty || ref === "core.money_amount" || (MONEY_IDS.test(id) && /decimal|integer|number/.test(resolved.kind))) return { role: "money" }
  if (/_minor$/.test(id) && resolved.kind === "integer") return { role: "money", minorUnits: true }
  if (ref === "core.quantity" || (QUANTITY_IDS.test(id) && /decimal|integer|number/.test(resolved.kind))) return { role: "quantity" }
  if (/date|time/.test(resolved.kind) || ref.startsWith("iso8601") || DATE_IDS.test(id)) return { role: "date" }
  if (STATUS_IDS.test(id) && (resolved.kind === "enum" || resolved.kind === "text")) return { role: "status" }
  if (ref === "core.phone_number" || ref === "core.email_address" || p.markings.some((m) => m.startsWith("pii"))) return { role: "contact", language }
  if (resolved.kind === "boolean") return { role: "flag" }
  if (/_name$/.test(id) && resolved.kind === "text" && !ctx.ids.has(`${id.replace(/_name$/, "")}`)) return { role: "title", language }
  return { role: "text", language }
}

function fieldLabel(p: RawProperty, role: FieldRole, link: RawLink | undefined): string {
  const imported = importedLabel(p.description)
  if (imported) return imported
  const id = memberName(p.id)
  if (link) return titleCase(memberName(link.id)).replace(/^./, (c) => c.toUpperCase())
  if (role === "money") return sentenceCase(id.replace(/_minor$/, ""))
  const lang = /_(en|ar)$/.exec(id)
  if (lang) return `${sentenceCase(id.slice(0, -3))} (${lang[1]?.toUpperCase()})`
  return sentenceCase(id)
}

/** Read the business ontology from an admitted definition set. Pure; memoize per set. */
export function readOntology(
  set: DefinitionSet,
  installedAppIds: readonly { appId: string; entryPageId?: string; pageNames?: readonly string[] }[] = [],
): Ontology {
  const catalog = propertyTypeCatalog(set)
  const gaps: string[] = []
  const extensions = new Map<string, { props: RawProperty[]; links: RawLink[] }>()
  const bases: { artifact: Artifact; body: Record<string, unknown> }[] = []
  for (const a of set.artifacts) {
    if (a.kind !== "entity") continue
    const p = a.payload
    if (isRecord(p.objectType)) bases.push({ artifact: a, body: p.objectType })
    else if (isRecord(p.objectTypeExtension)) {
      const ext = p.objectTypeExtension
      const target = asString(ext.target)
      if (!target) continue
      const ns = asString(ext.namespace) ?? a.namespace
      const entry = extensions.get(target) ?? { props: [], links: [] }
      entry.props.push(...readProperties(ext, ns))
      entry.links.push(...readLinks(ext, ns))
      extensions.set(target, entry)
    }
  }

  const types = new Map<string, TypeMeta>()
  for (const { artifact, body } of bases) {
    const name = asString(body.name) ?? artifact.artifactId
    const ext = extensions.get(name)
    const rawProps = [...readProperties(body), ...(ext?.props ?? [])]
    const rawLinks = [...readLinks(body), ...(ext?.links ?? [])]
    const identity = asArray(body.identity)
      .filter(isRecord)
      .flatMap((i) => asArray(i.properties))
      .filter((s): s is string => typeof s === "string")
    const lifecycle = lifecycleOf(body)
    const ids = new Set(rawProps.map((p) => memberName(p.id)))
    const imported = isRecord(artifact.payload.extensions)
      ? Object.entries(artifact.payload.extensions).find(([k]) => k.startsWith("norn.importers."))?.[1]
      : undefined
    const importInfo = isRecord(imported) ? imported : undefined
    const fields: FieldMeta[] = rawProps.map((p) => {
      const resolved = resolveType(p.type, catalog)
      const c = classify(p, resolved, { identity, links: rawLinks, lifecycle: lifecycle?.property, ids })
      const link = c.link ? rawLinks.find((l) => l.id === c.link) : undefined
      const options = resolved.options.length
        ? resolved.options
        : lifecycle?.property === p.id
          ? lifecycle.states.map((s) => ({ value: s, label: optionLabel(s) }))
          : []
      // A "status" whose type admits exactly one value (and no lifecycle) is a constant, not live state.
      const constant = c.role === "status" && lifecycle?.property !== p.id && resolved.options.length === 1
      return {
        id: p.id,
        label: fieldLabel(p, constant ? "text" : c.role, link),
        kind: resolved.kind,
        typeName: resolved.name ?? resolved.kind,
        role: constant ? "text" : c.role,
        description: p.description,
        options,
        markings: p.markings,
        link: c.link,
        ...(c.role === "money" && resolved.currencyProperty ? { currencyField: resolved.currencyProperty } : {}),
        minorUnits: c.minorUnits,
        language: c.language,
        extension: p.extension,
      }
    })
    // Money pairs with the currency beside it (Core: "an amount and an iso4217.currency_code beside it").
    const currency = fields.find((f) => f.role === "currency")
    const withCurrency = fields.map((f) => (f.role === "money" && currency && !f.currencyField ? { ...f, currencyField: currency.id } : f))

    const titles = withCurrency.filter((f) => f.role === "title")
    const titleFields = titles.length ? titles.map((f) => f.id) : []
    const label = asString(importInfo?.doctype) ?? titleCase(localName(name))
    if (!importInfo?.doctype) gaps.push(`${name}: no display name; "${label}" derived from the type name`)
    if (!titleFields.length && artifact.layer === "tenant") gaps.push(`${name}: no title property; records show their key`)
    const statusField = lifecycle?.property ?? withCurrency.find((f) => f.role === "status")?.id
    const dateField = withCurrency.find((f) => f.role === "date" && !f.extension)?.id
    const amountField =
      withCurrency.find((f) => f.role === "money" && /total|amount/.test(f.id) && !f.extension)?.id ??
      withCurrency.find((f) => f.role === "money" && !f.extension)?.id
    const links: LinkMeta[] = rawLinks.map((l) => ({
      id: l.id,
      label: importedLabel(l.description) ?? titleCase(memberName(l.id)),
      source: name,
      target: l.target,
      toMany: l.toMany,
      description: l.description,
      extension: l.extension,
    }))
    const namespace = artifact.namespace ?? name.split(".")[0] ?? name
    types.set(name, {
      name,
      namespace: name.includes(".") ? name.slice(0, name.lastIndexOf(".")) : namespace,
      label,
      pluralLabel: plural(label),
      description: asString(body.description),
      layer: artifact.layer,
      artifactId: artifact.artifactId,
      fields: withCurrency,
      links,
      identity,
      titleFields,
      statusField,
      dateField,
      amountField,
      lifecycle,
      implements: asArray(body.implements).filter((s): s is string => typeof s === "string"),
      source: asString(importInfo?.source),
      module: asString(importInfo?.module),
    })
  }

  // Amounts without a currency beside them take the only currency their area declares (else the
  // only one the whole ontology declares); with several candidates, none is assumed.
  const declared = (filter: (t: TypeMeta) => boolean) =>
    new Set([...types.values()].filter(filter).flatMap((t) => t.fields.filter((f) => f.role === "currency").flatMap((f) => f.options.map((o) => o.value))))
  const global = declared(() => true)
  for (const [name, t] of types) {
    const local = declared((x) => x.namespace === t.namespace)
    const only = local.size === 1 ? [...local][0] : local.size === 0 && global.size === 1 ? [...global][0] : undefined
    if (only) types.set(name, { ...t, fields: t.fields.map((f) => (f.role === "money" && !f.currencyField ? { ...f, currencyDefault: only } : f)) })
  }

  const incoming = new Map<string, LinkMeta[]>()
  for (const t of types.values())
    for (const l of t.links) {
      const list = incoming.get(l.target) ?? []
      list.push(l)
      incoming.set(l.target, list)
    }

  // Areas: one per namespace that holds tenant types, plus Core namespaces that tenant types link to.
  const tenantNamespaces = [...new Set([...types.values()].filter((t) => t.layer === "tenant").map((t) => t.namespace))]
  const linkedCore = new Set<string>()
  for (const t of types.values())
    for (const l of t.links) {
      const target = types.get(l.target)
      if (target && target.layer === "registry" && (t.layer === "tenant" || l.extension)) linkedCore.add(target.namespace)
      if (target && t.layer === "registry" && l.extension && target.layer === "tenant") linkedCore.add(t.namespace)
    }
  const prefix = sharedPrefix(tenantNamespaces)
  const areaIds = [...tenantNamespaces, ...[...linkedCore].filter((ns) => !tenantNamespaces.includes(ns))]
  const areas: AreaMeta[] = areaIds.map((id) => {
    const core = !tenantNamespaces.includes(id)
    const members = [...types.values()].filter((t) => t.namespace === id && (!core || incoming.has(t.name) || t.links.some((l) => l.extension)))
    return {
      id,
      label: areaLabel(id, prefix, installedAppIds),
      types: members.map((t) => t.name),
      core,
      apps: installedAppIds.filter((app) => app.appId === id || app.appId.startsWith(`${id}.`)),
    }
  })
  const areaOf = new Map<string, string>()
  for (const area of areas) for (const t of area.types) areaOf.set(t, area.id)
  const relationMap = new Map<string, LinkMeta[]>()
  for (const t of types.values())
    for (const l of t.links) {
      const from = areaOf.get(l.source)
      const to = areaOf.get(l.target)
      if (!from || !to || from === to) continue
      const key = `${from}\u0000${to}`
      relationMap.set(key, [...(relationMap.get(key) ?? []), l])
    }
  const relations = [...relationMap.entries()].map(([key, links]) => {
    const [from, to] = key.split("\u0000") as [string, string]
    return { from, to, links }
  })
  return { types, areas: areas.filter((a) => a.types.length > 0), relations, incoming, gaps }
}

/** An installed app's own overview page names its area ("Retail overview" → Retail); else the namespace. */
function areaLabel(id: string, prefix: string, apps: readonly { appId: string; pageNames?: readonly string[] }[]): string {
  for (const app of apps) {
    if (app.appId !== id && !app.appId.startsWith(`${id}.`)) continue
    for (const name of app.pageNames ?? []) {
      const m = /^(.+?)\s+overview$/i.exec(name.trim())
      if (m?.[1]) return m[1]
    }
  }
  return titleCase(prefix && id.startsWith(prefix) ? id.slice(prefix.length) : id)
}

export function areaOfType(ontology: Ontology, typeName: string): AreaMeta | undefined {
  return ontology.areas.find((a) => a.types.includes(typeName))
}

/** The record's title in the reader's language, from the type's title fields; null when none is readable. */
export function recordTitle(
  type: TypeMeta | undefined,
  values: Record<string, unknown>,
  masked: Record<string, unknown> | undefined,
  locale: string,
): { title: string | null; alternate: string | null; hidden: boolean } {
  if (!type) return { title: null, alternate: null, hidden: false }
  const readable = (id: string) => {
    const v = values[id]
    return !masked?.[id] && typeof v === "string" && v.trim() !== "" ? v : null
  }
  const ordered = [...type.titleFields].sort((a, b) => score(type, b, locale) - score(type, a, locale))
  const present = ordered.map(readable).filter((v): v is string => v !== null)
  const hidden = type.titleFields.length > 0 && type.titleFields.every((id) => Boolean(masked?.[id]))
  const [title = null, alternate = null] = [...new Set(present)]
  return { title, alternate, hidden }
}

function score(type: TypeMeta, id: string, locale: string): number {
  const f = type.fields.find((x) => x.id === id)
  if (!f) return 0
  return (f.language === locale ? 4 : f.language ? 1 : 2) + (memberName(id) === "display_name" ? 1 : 0)
}

/** The fields a list shows after its title column, in reading priority, at most `max`. */
export function listFields(type: TypeMeta, max = 5): FieldMeta[] {
  const rank: Partial<Record<FieldRole, number>> = { status: 0, reference: 1, money: 2, date: 3, quantity: 4, text: 6, flag: 7, contact: 8 }
  return type.fields
    .filter((f) => f.role in rank && !f.extension && !(f.role === "contact" && f.language))
    .map((f, i) => ({ f, r: (rank[f.role] ?? 9) * 100 + i }))
    .sort((a, b) => a.r - b.r)
    .slice(0, max)
    .map(({ f }) => f)
}
